import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, ChevronRight, MapPin, Phone, Mail, Instagram, Linkedin, Facebook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/aNP96AvL6N9w1zfc85zs3T/f8ec9bf5-99b8-4cc8-8dd9-352e670d3645.png';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'About',
    path: '/about'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <div className="min-h-screen flex flex-col bg-navy-900 text-white font-body selection:bg-gold selection:text-navy-900" data-id="element-17">
      {/* Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-navy-900/95 backdrop-blur-md py-3 shadow-soft' : 'bg-transparent py-6'}`} data-id="element-18">
        <div className="max-w-content mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-19">
          <Link to="/" className="flex items-center gap-3 z-50" data-id="element-20">
            <img src={LOGO_URL} alt="Marvesta Corp Bali" className="h-12 w-auto object-contain" data-id="element-21" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" data-id="element-22">
            {navLinks.map(link => <Link key={link.name} to={link.path} className={`font-sub text-sm uppercase tracking-wider transition-colors hover:text-gold ${location.pathname === link.path ? 'text-gold font-medium' : 'text-gray-300'}`} data-id="element-23">
                {link.name}
              </Link>)}
            <Link to="/contact" className="bg-gold hover:bg-gold-light text-navy-900 font-sub font-semibold py-2.5 px-6 rounded-btn transition-all duration-300 shadow-gold hover:shadow-none text-sm uppercase tracking-wider" data-id="element-24">
              Book Consultation
            </Link>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden text-white z-50 p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu" data-id="element-25">
            {mobileMenuOpen ? <X size={28} data-id="element-26" /> : <Menu size={28} data-id="element-27" />}
          </button>
        </div>

        {/* Mobile Nav */}
        <AnimatePresence data-id="element-28">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="absolute top-full left-0 right-0 bg-navy-800 border-t border-navy-700 shadow-card lg:hidden" data-id="element-29">
              <div className="flex flex-col px-6 py-8 gap-6" data-id="element-30">
                {navLinks.map(link => <Link key={link.name} to={link.path} className={`font-sub text-lg uppercase tracking-wider ${location.pathname === link.path ? 'text-gold' : 'text-white'}`} data-id="element-31">
                    {link.name}
                  </Link>)}
                <Link to="/contact" className="bg-gold text-navy-900 font-sub font-semibold py-3 px-6 rounded-btn text-center mt-4 uppercase tracking-wider" data-id="element-32">
                  Book Consultation
                </Link>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-33">{children}</main>

      {/* Footer */}
      <footer className="bg-navy-900 border-t border-navy-700 pt-20 pb-10" data-id="element-34">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-35">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16" data-id="element-36">
            <div data-id="element-37">
              <img src={LOGO_URL} alt="Marvesta Corp Bali" className="h-16 w-auto object-contain mb-6" data-id="element-38" />
              <p className="text-gray-400 font-body text-sm leading-relaxed mb-6" data-id="element-39">
                Your trusted partner for Property Management, Accounting, Tax,
                Legal, and Business Support in Bali. Delivering premium
                corporate solutions with international standards.
              </p>
              <div className="flex gap-4" data-id="element-40">
                <a href="https://www.instagram.com/marvestabali" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center text-gold hover:bg-gold hover:text-navy-900 transition-colors" data-id="element-41">
                  <Instagram size={18} data-id="element-42" />
                </a>
                <a href="https://www.linkedin.com/in/marvesta-corp-bali" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center text-gold hover:bg-gold hover:text-navy-900 transition-colors" data-id="element-43">
                  <Linkedin size={18} data-id="element-44" />
                </a>
                <a href="https://www.facebook.com/share/1Ciyxg2qZU/" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-navy-800 flex items-center justify-center text-gold hover:bg-gold hover:text-navy-900 transition-colors" data-id="element-45">
                  <Facebook size={18} data-id="element-46" />
                </a>
              </div>
            </div>

            <div data-id="element-47">
              <h4 className="font-heading text-xl font-semibold text-white mb-6" data-id="element-48">
                Services
              </h4>
              <ul className="space-y-4" data-id="element-49">
                <li data-id="element-50">
                  <Link to="/property-management" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-51">
                    <ChevronRight size={14} className="text-gold" data-id="element-52" /> Property
                    Management
                  </Link>
                </li>
                <li data-id="element-53">
                  <Link to="/business-finance-services" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-54">
                    <ChevronRight size={14} className="text-gold" data-id="element-55" /> Accounting
                    & Tax
                  </Link>
                </li>
                <li data-id="element-56">
                  <Link to="/business-finance-services" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-57">
                    <ChevronRight size={14} className="text-gold" data-id="element-58" /> Audit
                    Services
                  </Link>
                </li>
                <li data-id="element-59">
                  <Link to="/legal-licensing-expat-services" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-60">
                    <ChevronRight size={14} className="text-gold" data-id="element-61" /> Legal &
                    Licensing
                  </Link>
                </li>
                <li data-id="element-62">
                  <Link to="/legal-licensing-expat-services" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-63">
                    <ChevronRight size={14} className="text-gold" data-id="element-64" /> Visa &
                    Expat Support
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-65">
              <h4 className="font-heading text-xl font-semibold text-white mb-6" data-id="element-66">
                Company
              </h4>
              <ul className="space-y-4" data-id="element-67">
                <li data-id="element-68">
                  <Link to="/about" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-69">
                    <ChevronRight size={14} className="text-gold" data-id="element-70" /> About Us
                  </Link>
                </li>
                <li data-id="element-71">
                  <Link to="/services" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-72">
                    <ChevronRight size={14} className="text-gold" data-id="element-73" /> All
                    Services
                  </Link>
                </li>
                <li data-id="element-74">
                  <Link to="/contact" className="text-gray-400 hover:text-gold transition-colors flex items-center gap-2 text-sm" data-id="element-75">
                    <ChevronRight size={14} className="text-gold" data-id="element-76" /> Contact
                  </Link>
                </li>
              </ul>
            </div>

            <div data-id="element-77">
              <h4 className="font-heading text-xl font-semibold text-white mb-6" data-id="element-78">
                Contact Us
              </h4>
              <ul className="space-y-4" data-id="element-79">
                <li className="flex items-start gap-3 text-gray-400 text-sm" data-id="element-80">
                  <MapPin size={18} className="text-gold shrink-0 mt-0.5" data-id="element-81" />
                  <span data-id="element-82">
                    Bali, Indonesia
                    <br data-id="element-83" />
                    Serving clients nationwide
                  </span>
                </li>
                <li className="flex items-center gap-3 text-gray-400 text-sm" data-id="element-84">
                  <Phone size={18} className="text-gold shrink-0" data-id="element-85" />
                  <a href="https://wa.me/6282326357189" className="hover:text-gold transition-colors" data-id="element-86">
                    +62 823-2635-7189
                  </a>
                </li>
                <li className="flex items-center gap-3 text-gray-400 text-sm" data-id="element-87">
                  <Mail size={18} className="text-gold shrink-0" data-id="element-88" />
                  <a href="mailto:marvestafinance@gmail.com" className="hover:text-gold transition-colors" data-id="element-89">
                    marvestafinance@gmail.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-navy-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-90">
            <p className="text-gray-500 text-sm" data-id="element-91">
              &copy; {new Date().getFullYear()} Marvesta Corp Bali. All rights
              reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500" data-id="element-92">
              <Link to="#" className="hover:text-gold transition-colors" data-id="element-93">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-gold transition-colors" data-id="element-94">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a href="https://wa.me/6282326357189" target="_blank" rel="noreferrer" className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-4 rounded-full shadow-card hover:scale-110 transition-transform duration-300 flex items-center justify-center group" aria-label="Contact on WhatsApp" data-id="element-95">
        <MessageCircle size={28} data-id="element-96" />
        <span className="absolute right-full mr-4 bg-navy-800 text-white text-sm py-2 px-4 rounded-card opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-soft" data-id="element-97">
          Chat with us
        </span>
      </a>
    </div>;
}